import React from 'react';
import SparkFormStepper from 'sharedComponents/UI/SparkStepper';
import { Box } from '@mui/material';
import {
  ON_BOARDING_FORM_STEPS,
  ON_BOARDING_STEPS_CONFIG,
} from 'constants/onBoardingForm';
import CompanyBasicInfoFrom, {
  type CompanyBasicInfoFromValues,
} from 'components/AuthScreens/Register/Stepper/CompanyBasicInfoFrom';
import CompanyOverViewForm, {
  type CompanyOverViewFormValues,
} from 'components/AuthScreens/Register/Stepper/CompanyOverViewForm';
import ChallengesForm, {
  type ChallengesFormValues,
} from 'components/AuthScreens/Register/Stepper/ChallengesForm';
import ObjectivesForm, {
  type ObjectivesFormValues,
} from 'components/AuthScreens/Register/Stepper/ObjectivesForm';
import { useAddCompanyOverview } from 'hooks/CompanyOverview/useAddCompanyOverview';
import { useAddCompanyOverviewKeyObjectives } from 'hooks/CompanyOverview/useAddCompanyOverviewKeyObjectives';
import { useAddCompanyOverviewMainChallenges } from 'hooks/CompanyOverview/useAddCompanyOverviewMainChallenges';
import { useQueryClient } from 'react-query';
import { useAddCompanyOverviewMarketApproaches } from 'hooks/CompanyOverview/useAddCompanyOverviewMarketApproach';

type CollectiveValuesType = {
  1?: CompanyBasicInfoFromValues;
  2?: CompanyOverViewFormValues;
  3?: ObjectivesFormValues;
  4?: ChallengesFormValues;
};
type CompanyInfoStepperFormProps = {
  shouldRenderWelcomeText?: boolean;
  onSave: () => void;
  categoryID?: string | null;
  onResetCategory?: () => void;
};

function CompanyInfoStepperForm({
  shouldRenderWelcomeText,
  onSave,
  categoryID,
  onResetCategory,
}: CompanyInfoStepperFormProps) {
  const [activeStep, setActiveStep] = React.useState<number>(1);

  const collectiveValues = React.useRef<CollectiveValuesType | null>(null);

  const queryClient = useQueryClient();
  const { mutateAsync: addCompanyOverView } = useAddCompanyOverview();
  const { mutateAsync: addCompanyOverViewKeyObjectives } =
    useAddCompanyOverviewKeyObjectives();
  const { mutateAsync: addCompanyOverViewMainChallenges } =
    useAddCompanyOverviewMainChallenges();

  const { mutateAsync: addCompanyOverViewMarketApproach } =
    useAddCompanyOverviewMarketApproaches();

  const setBasicInfoCollection = (formValues: CompanyBasicInfoFromValues) => {
    const previous = collectiveValues.current || {};
    collectiveValues.current = { ...previous, 1: formValues };
  };
  const setOverViewCollection = (formValues: CompanyOverViewFormValues) => {
    const previous = collectiveValues.current || {};
    collectiveValues.current = { ...previous, 2: formValues };
  };
  const setObjectivesCollection = (formValues: ObjectivesFormValues) => {
    const previous = collectiveValues.current || {};
    collectiveValues.current = { ...previous, 3: formValues };
  };
  const setChallengesCollection = (formValues: ChallengesFormValues) => {
    const previous = collectiveValues.current || {};
    collectiveValues.current = { ...previous, 4: formValues };
  };

  const handleSubmit = async () => {
    if (!collectiveValues) {
      return;
    }
    const { companyName, industryID, countryID, coreOffering } =
      collectiveValues.current?.[1] || {};
    const {
      companyStageID,
      legalStructureID,
      geographicMarketID,
      marketApproaches,
    } = collectiveValues.current?.[2] || {};
    const { otherObjective, objectiveIDs } =
      collectiveValues.current?.[3] || {};
    const { challengesIDs, otherChallenge } =
      collectiveValues.current?.[4] || {};
    if (!objectiveIDs?.length || !challengesIDs?.length) {
      return;
    }

    const response = await addCompanyOverView({
      companyName,
      countryID: countryID ? +countryID : undefined,
      businessIndustryID: industryID ? +industryID : undefined,
      coreOffering,
      businessStageID: companyStageID ? +companyStageID : undefined,
      legalStructureID: legalStructureID ? +legalStructureID : undefined,
      geographicMarketID: geographicMarketID ? +geographicMarketID : undefined,
      otherKeyObjectives: objectiveIDs?.includes('10') ? otherObjective : '',
      otherMainChallenges: challengesIDs?.includes('10') ? otherChallenge : '',
    });
    const companyOverviewID = response.data.id;

    if (marketApproaches) {
      const marketApproach = marketApproaches.map((marketApproachID) => {
        return addCompanyOverViewMarketApproach({
          marketApproachID: +marketApproachID,
          companyOverviewID,
        });
      });

      await Promise.all(marketApproach);
    }
    const objectivePromises = objectiveIDs.map((objectiveID) => {
      return addCompanyOverViewKeyObjectives({
        keyObjectiveID: +objectiveID,
        companyOverviewID,
      });
    });

    await Promise.all(objectivePromises);

    const challengePromises = challengesIDs.map((challengeID) => {
      return addCompanyOverViewMainChallenges({
        mainChallengeID: +challengeID,
        companyOverviewID,
      });
    });

    await Promise.all(challengePromises);
    queryClient.invalidateQueries('ApplicationUser');
    queryClient.invalidateQueries(['workspaces']);
    onSave();
  };
  const nextStepHandler = async () => {
    const currentStepConfig = ON_BOARDING_STEPS_CONFIG.find(
      ({ formKey }) => formKey === activeStep
    );
    if (!currentStepConfig) {
      return;
    }
    const { nextStepFormKey } = currentStepConfig || {};
    if (nextStepFormKey) {
      setActiveStep(nextStepFormKey);
      return;
    }
    handleSubmit();
  };
  const previousStepHandler = () => {
    if (activeStep === 1) {
      onResetCategory && onResetCategory();
      return;
    }
    setActiveStep((prev) => {
      if (prev === 1) {
        return prev;
      }
      return prev - 1;
    });
  };
  const getCurrentStep = () => {
    switch (activeStep) {
      case ON_BOARDING_FORM_STEPS.COMPANY_BASIC_INFO_ID:
        return (
          <CompanyBasicInfoFrom
            shouldRenderWelcomeText={shouldRenderWelcomeText}
            nextStepHandler={nextStepHandler}
            previousStepHandler={previousStepHandler}
            activeStep={activeStep}
            basicInfoCollection={collectiveValues.current?.[1]}
            setBasicInfoCollection={setBasicInfoCollection}
          />
        );
      case ON_BOARDING_FORM_STEPS.COMPANY_OVER_VIEW_ID:
        return (
          <CompanyOverViewForm
            nextStepHandler={nextStepHandler}
            previousStepHandler={previousStepHandler}
            activeStep={activeStep}
            overViewCollection={collectiveValues.current?.[2]}
            setOverViewCollection={setOverViewCollection}
          />
        );
      case ON_BOARDING_FORM_STEPS.OBJECTIVES_FORM_ID:
        return (
          <ObjectivesForm
            nextStepHandler={nextStepHandler}
            previousStepHandler={previousStepHandler}
            activeStep={activeStep}
            objectivesCollection={collectiveValues.current?.[3]}
            setObjectivesCollection={setObjectivesCollection}
            categoryID={categoryID}
          />
        );
      case ON_BOARDING_FORM_STEPS.CHALLENGES_FORM_ID:
        return (
          <ChallengesForm
            nextStepHandler={nextStepHandler}
            previousStepHandler={previousStepHandler}
            activeStep={activeStep}
            challengesCollection={collectiveValues.current?.[4]}
            setChallengesCollection={setChallengesCollection}
            categoryID={categoryID}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '600px !important' }} marginTop={3}>
      <SparkFormStepper
        stepIDs={ON_BOARDING_STEPS_CONFIG.map((step) => step.formKey)}
        activeStep={activeStep}
      />
      <div>{getCurrentStep()}</div>
    </Box>
  );
}

export default CompanyInfoStepperForm;
